import {FC} from 'react'
import {Card, Form, Row, Col, Button, Spinner} from 'react-bootstrap'
import Select from 'react-select'
import {AgreementDetail, AgreementSource} from '../core/_models'
import {ColumnDef} from '@tanstack/react-table'
import DataTable from '../../../../../ui-kit/DataTable'

type Props = {
  isLoadingAgreement: boolean
  agreementOrigin: AgreementDetail
  agreementPending: AgreementDetail
  agreementType: any[]
  agreementStatus: any[]
  agreementNotification: any[]
  countries: any[]
  agreementComposition: any[]
  originalSources: AgreementSource[]
  pendingSources: AgreementSource[]
  sourceColumns: ColumnDef<AgreementSource, any>[]
  isDifferent: (val1: any, val2: any) => boolean
  handleAgreementChange: (e: any) => void
  handlePartiesChange: (selected: any) => void
  setAgreementPending: React.Dispatch<React.SetStateAction<AgreementDetail>>
  setShowSourceModal: (show: boolean) => void
  handleCancel: () => void
  handleSubmit: () => void
  isLoadingButton: boolean
  isFormValid: boolean
}

const AgreementComparisonForm: FC<Props> = ({
  isLoadingAgreement,
  agreementOrigin,
  agreementPending,
  agreementType,
  agreementStatus,
  agreementNotification,
  countries,
  agreementComposition,
  originalSources,
  pendingSources,
  sourceColumns,
  isDifferent,
  handleAgreementChange,
  handlePartiesChange,
  setAgreementPending,
  setShowSourceModal,
  handleCancel,
  handleSubmit,
  isLoadingButton,
  isFormValid,
}) => {
  return (
    <Row className='mb-5'>
      <Col>
        <Card>
          <Card.Header className='card-header-stretch'>
            <Card.Title className='w-100'>
              <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                <div className='title d-flex align-items-center'>
                  <span className='fa fa-balance-scale px-2'></span>
                  <h3 className='text-gray-700'>Review Agreement Differences</h3>
                </div>
              </div>
            </Card.Title>
          </Card.Header>
          <Card.Body>
            {!isLoadingAgreement ? (
              <Form className='form-compare-agreement'>
                {/* HEADERS */}
                <Row className='mb-4 pb-2 border-bottom'>
                  <Col md={6}>
                    <h4 className='text-muted text-center'>Origin Content</h4>
                  </Col>
                  <Col md={6}>
                    <h4 className='text-primary text-center'>Pending Review Content</h4>
                  </Col>
                </Row>

                {/* GENERAL INFORMATION */}
                <h3 className='mb-4 mt-5'>General Information</h3>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Name
                    </Form.Label>
                    <Form.Control disabled value={agreementOrigin.agreement} />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(agreementOrigin.agreement, agreementPending.agreement)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        Name
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='text'
                      name='agreement'
                      className='required'
                      value={agreementPending.agreement}
                      onChange={handleAgreementChange}
                    />
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Short Name
                    </Form.Label>
                    <Form.Control disabled value={agreementOrigin.acronym} />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(agreementOrigin.acronym, agreementPending.acronym)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        Short Name
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='text'
                      name='acronym'
                      className='required'
                      value={agreementPending.acronym}
                      onChange={handleAgreementChange}
                    />
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Type
                    </Form.Label>
                    <Select
                      isDisabled
                      isMulti
                      styles={{
                        valueContainer: (baseStyles) => ({
                          ...baseStyles,
                          height: '3.27rem',
                          overflowY: 'auto',
                        }),
                      }}
                      name='type'
                      options={agreementType}
                      value={agreementOrigin.type?.map((n: string) => ({value: n, label: n})) || []}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(agreementOrigin.type, agreementPending.type)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        Type
                      </span>
                    </Form.Label>
                    <Select
                      isMulti
                      name='type'
                      options={agreementType}
                      value={
                        agreementPending.type?.map((n: string) => ({value: n, label: n})) || []
                      }
                      onChange={(selected) =>
                        setAgreementPending((prev) => ({
                          ...prev,
                          type: selected.map((s) => s.value),
                        }))
                      }
                    />
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Status
                    </Form.Label>
                    <Form.Select disabled value={agreementOrigin.status}>
                      <option value='' disabled>
                        Select status
                      </option>
                      {agreementStatus.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(agreementOrigin.status, agreementPending.status)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        Status
                      </span>
                    </Form.Label>
                    <Form.Select
                      name='status'
                      className='required'
                      value={agreementPending.status}
                      onChange={handleAgreementChange}
                    >
                      <option value='' disabled>
                        Select status
                      </option>
                      {agreementStatus.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      WTO RTA Id
                    </Form.Label>
                    <Form.Control disabled value={agreementOrigin.wto_rta_id} />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(agreementOrigin.wto_rta_id, agreementPending.wto_rta_id)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        WTO RTA Id
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='text'
                      name='wto_rta_id'
                      value={agreementPending.wto_rta_id}
                      onChange={handleAgreementChange}
                    />
                    <a
                      href='https://rtais.wto.org/UI/PublicAllRTAList.aspx'
                      target='_blank'
                      rel='noreferrer'
                      className='text-link'
                    >
                      check the WTO database
                    </a>
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Notification
                    </Form.Label>
                    <Select
                      isDisabled
                      isMulti
                      options={agreementNotification}
                      value={
                        agreementOrigin.notification?.map((n: string) => ({value: n, label: n})) ||
                        []
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={
                          isDifferent(agreementOrigin.notification, agreementPending.notification)
                            ? 'label-highlight'
                            : ''
                        }
                      >
                        Notification
                      </span>
                    </Form.Label>
                    <Select
                      isMulti
                      options={agreementNotification}
                      value={
                        agreementPending.notification?.map((n: string) => ({value: n, label: n})) ||
                        []
                      }
                      onChange={(selected) =>
                        setAgreementPending((prev) => ({
                          ...prev,
                          notification: selected.map((s) => s.value),
                        }))
                      }
                    />
                  </Col>
                </Row>

                <hr />
                {/* DATE INFORMATION */}
                <h3 className='mb-4 mt-5'>Date Information</h3>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Date Signed
                    </Form.Label>
                    <Form.Control disabled type='date' value={agreementOrigin.date_signed} />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={
                          isDifferent(agreementOrigin.date_signed, agreementPending.date_signed)
                            ? 'label-highlight'
                            : ''
                        }
                      >
                        Date Signed
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='date'
                      name='date_signed'
                      value={agreementPending.date_signed}
                      onChange={handleAgreementChange}
                    />
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Date Into Force
                    </Form.Label>
                    <Form.Control disabled type='date' value={agreementOrigin.date_into_force} />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={
                          isDifferent(
                            agreementOrigin.date_into_force,
                            agreementPending.date_into_force
                          )
                            ? 'label-highlight'
                            : ''
                        }
                      >
                        Date Into Force
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='date'
                      name='date_into_force'
                      value={agreementPending.date_into_force}
                      onChange={handleAgreementChange}
                    />
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Date Notification
                    </Form.Label>
                    <Form.Control disabled type='date' value={agreementOrigin.date_notification} />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={
                          isDifferent(
                            agreementOrigin.date_notification,
                            agreementPending.date_notification
                          )
                            ? 'label-highlight'
                            : ''
                        }
                      >
                        Date Notification
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='date'
                      name='date_notification'
                      value={agreementPending.date_notification}
                      onChange={handleAgreementChange}
                    />
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Date of Implementation
                    </Form.Label>
                    <Form.Control
                      disabled
                      type='date'
                      value={agreementOrigin.date_of_implementation}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={
                          isDifferent(
                            agreementOrigin.date_of_implementation,
                            agreementPending.date_of_implementation
                          )
                            ? 'label-highlight'
                            : ''
                        }
                      >
                        Date of Implementation
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='date'
                      name='date_of_implementation'
                      value={agreementPending.date_of_implementation}
                      onChange={handleAgreementChange}
                    />
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      End Implementation
                    </Form.Label>
                    <Form.Control disabled type='date' value={agreementOrigin.end_implementation} />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={
                          isDifferent(
                            agreementOrigin.end_implementation,
                            agreementPending.end_implementation
                          )
                            ? 'label-highlight'
                            : ''
                        }
                      >
                        End Implementation
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='date'
                      name='end_implementation'
                      value={agreementPending.end_implementation}
                      onChange={handleAgreementChange}
                    />
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Date Inactive
                    </Form.Label>
                    <Form.Control disabled type='date' value={agreementOrigin.date_inactive} />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={
                          isDifferent(agreementOrigin.date_inactive, agreementPending.date_inactive)
                            ? 'label-highlight'
                            : ''
                        }
                      >
                        Date Inactive
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='date'
                      name='date_inactive'
                      value={agreementPending.date_inactive}
                      onChange={handleAgreementChange}
                    />
                  </Col>
                </Row>

                <hr />
                {/* PARTICIPATION & SCOPE */}
                <h3 className='mb-4 mt-5'>Participation & Scope</h3>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Parties
                    </Form.Label>
                    <Select
                      isDisabled
                      isMulti
                      options={countries}
                      value={
                        agreementOrigin.iso
                          ?.map((iso) => countries.find((option) => option.value === iso))
                          .filter(Boolean) || []
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(agreementOrigin.iso, agreementPending.iso)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        Parties
                      </span>
                    </Form.Label>
                    <Select
                      isMulti
                      options={countries}
                      value={
                        agreementPending.iso
                          ?.map((iso) => countries.find((option) => option.value === iso))
                          .filter(Boolean) || []
                      }
                      onChange={handlePartiesChange}
                    />
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Composition
                    </Form.Label>
                    <Form.Select disabled value={agreementOrigin.composition}>
                      <option value='' disabled>
                        Select composition
                      </option>
                      {agreementComposition.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(agreementOrigin.composition, agreementPending.composition)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        Composition
                      </span>
                    </Form.Label>
                    <Form.Select
                      name='composition'
                      className='required'
                      value={agreementPending.composition}
                      onChange={handleAgreementChange}
                    >
                      <option value='' disabled>
                        Select composition
                      </option>
                      {agreementComposition.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Parties WTO
                    </Form.Label>
                    <Form.Select disabled value={agreementOrigin.parties_wto}>
                      <option value='' disabled>
                        Select parties WTO
                      </option>
                      <option value='yes'>Yes</option>
                      <option value='no'>No</option>
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(agreementOrigin.parties_wto, agreementPending.parties_wto)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        Parties WTO
                      </span>
                    </Form.Label>
                    <Form.Select
                      name='parties_wto'
                      className='required'
                      value={agreementPending.parties_wto}
                      onChange={handleAgreementChange}
                    >
                      <option value='' disabled>
                        Select parties WTO
                      </option>
                      <option value='yes'>Yes</option>
                      <option value='no'>No</option>
                    </Form.Select>
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Region
                    </Form.Label>
                    <textarea
                      className='form-control'
                      rows={2}
                      value={agreementOrigin.region}
                      disabled
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(agreementOrigin.region, agreementPending.region)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        Region
                      </span>
                    </Form.Label>
                    <textarea
                      className='form-control'
                      rows={2}
                      name='region'
                      value={agreementPending.region}
                      disabled
                    />
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Crossregional
                    </Form.Label>
                    <Form.Control
                      disabled
                      className='text-capitalize'
                      value={agreementOrigin.crossregional}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(agreementOrigin.crossregional, agreementPending.crossregional)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        Crossregional
                      </span>
                    </Form.Label>
                    <Form.Control
                      className='text-capitalize'
                      name='crossregional'
                      value={agreementPending.crossregional}
                      disabled
                    />
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Parties ESCAP
                    </Form.Label>
                    <Form.Control
                      disabled
                      className='text-capitalize'
                      value={agreementOrigin.parties_escap}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={
                          isDifferent(agreementOrigin.parties_escap, agreementPending.parties_escap)
                            ? 'label-highlight'
                            : ''
                        }
                      >
                        Parties ESCAP
                      </span>
                    </Form.Label>
                    <Form.Control
                      className='text-capitalize'
                      name='parties_escap'
                      value={agreementPending.parties_escap}
                      disabled
                    />
                  </Col>
                </Row>

                <hr />
                {/* OTHERS */}
                <h3 className='mb-4 mt-5'>Others</h3>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Language
                    </Form.Label>
                    <Form.Control disabled value={agreementOrigin.language} />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={
                          isDifferent(agreementOrigin.language, agreementPending.language)
                            ? 'label-highlight'
                            : ''
                        }
                      >
                        Language
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='text'
                      name='language'
                      value={agreementPending.language}
                      onChange={handleAgreementChange}
                    />
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Service Schedule
                    </Form.Label>
                    <Form.Select disabled value={agreementOrigin.services_schedules}>
                      <option value='' disabled>
                        Select service schedule
                      </option>
                      <option value='yes'>Yes</option>
                      <option value='no'>No</option>
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={
                          isDifferent(
                            agreementOrigin.services_schedules,
                            agreementPending.services_schedules
                          )
                            ? 'label-highlight'
                            : ''
                        }
                      >
                        Service Schedule
                      </span>
                    </Form.Label>
                    <Form.Select
                      name='services_schedules'
                      value={agreementPending.services_schedules}
                      onChange={handleAgreementChange}
                    >
                      <option value='' disabled>
                        Select service schedule
                      </option>
                      {agreementPending.services_schedules && (
                        <option value=''>-- Clear selection --</option>
                      )}
                      <option value='yes'>Yes</option>
                      <option value='no'>No</option>
                    </Form.Select>
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      WITS Id
                    </Form.Label>
                    <Form.Control disabled value={agreementOrigin.wits_id} />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(agreementOrigin.wits_id, agreementPending.wits_id)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        WITS Id
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='text'
                      name='wits_id'
                      value={agreementPending.wits_id}
                      onChange={handleAgreementChange}
                    />
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Desta Id
                    </Form.Label>
                    <Form.Control disabled value={agreementOrigin.desta_id} />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={
                          isDifferent(agreementOrigin.desta_id, agreementPending.desta_id)
                            ? 'label-highlight'
                            : ''
                        }
                      >
                        Desta Id
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='text'
                      name='desta_id'
                      value={agreementPending.desta_id}
                      onChange={handleAgreementChange}
                    />
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Tota Id
                    </Form.Label>
                    <Form.Control disabled value={agreementOrigin.tota_id} />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(agreementOrigin.tota_id, agreementPending.tota_id)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        Tota Id
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='text'
                      name='tota_id'
                      value={agreementPending.tota_id}
                      onChange={handleAgreementChange}
                    />
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Aptiad Id
                    </Form.Label>
                    <Form.Control disabled value={agreementOrigin.aptiad_id} />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(agreementOrigin.aptiad_id, agreementPending.aptiad_id)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        Aptiad Id
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='text'
                      name='aptiad_id'
                      value={agreementPending.aptiad_id}
                      onChange={handleAgreementChange}
                    />
                  </Col>
                </Row>

                <hr />
                {/* SOURCES */}
                <Row className='align-items-end mb-4 mt-5'>
                  <Col md={6} className='d-flex align-items-center justify-content-between'>
                    <h3 className='text-muted'>Sources (Origin)</h3>
                  </Col>
                  <Col md={6} className='d-flex align-items-center justify-content-between'>
                    <h3
                      className={`text-primary ${
                        isDifferent(agreementOrigin.sources, agreementPending.sources)
                          ? 'label-highlight'
                          : ''
                      }`}
                    >
                      Sources (Pending)
                    </h3>
                    <Button onClick={() => setShowSourceModal(true)}>Add Source</Button>
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <DataTable<AgreementSource>
                      data={originalSources}
                      columns={sourceColumns}
                      enablePagination={false}
                      enableSorting={true}
                      enableGlobalFilter={false}
                      className='my-custom-table-wrapper'
                    />
                  </Col>
                  <Col md={6}>
                    <DataTable<AgreementSource>
                      data={pendingSources}
                      columns={sourceColumns}
                      enablePagination={false}
                      enableSorting={true}
                      enableGlobalFilter={false}
                      className='my-custom-table-wrapper'
                    />
                  </Col>
                </Row>

                <div className='row pt-5 border-top border-gray-200 mt-10'>
                  <div className='col-lg-12 d-flex justify-content-end gap-3'>
                    <Button variant='default' className='btn-cancel' onClick={handleCancel}>
                      Cancel
                    </Button>
                    <Button
                      variant='primary'
                      onClick={handleSubmit}
                      disabled={isLoadingButton || !isFormValid}
                    >
                      {isLoadingButton ? (
                        <>
                          <Spinner animation='border' size='sm' className='me-2' />
                          Submit
                        </>
                      ) : (
                        'Submit'
                      )}
                    </Button>
                  </div>
                </div>
              </Form>
            ) : (
              <div
                className='d-flex align-items-center justify-content-center'
                style={{height: '50vh'}}
              >
                <Spinner animation='border' role='status' variant='primary' className='me-3' />
                <div className='loading-text'>
                  Loading
                  <span className='dot'>.</span>
                  <span className='dot'>.</span>
                  <span className='dot'>.</span>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  )
}

export default AgreementComparisonForm
